import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["table", "hiddenInput", "searchInput"]
  static values = {
    data: Object
  }

  connect() {
    this.rows = []
    if (this.dataValue && Object.keys(this.dataValue).length > 0) {
      this.flattenData(this.dataValue)
    }
    this.render()
    this.updateHiddenInput()
  }

  // Flatten nested JSON into rows with key paths
  flattenData(obj, path = []) {
    if (!obj || typeof obj !== "object") return

    for (const [key, value] of Object.entries(obj)) {
      const currentPath = [...path, key]

      if (this.isLeafNode(value)) {
        this.rows.push({
          id: crypto.randomUUID(),
          keyPath: currentPath,
          match: value._match || "match",
          values: this.normalizeValues(value._value)
        })
      } else if (typeof value === "object") {
        this.flattenData(value, currentPath)
      }
    }
  }

  isLeafNode(value) {
    return value && typeof value === "object" && "_value" in value
  }

  normalizeValues(value) {
    if (Array.isArray(value)) {
      return value.map(v => String(v ?? ""))
    }
    return [String(value ?? "")]
  }

  render() {
    const searchTerm = this.hasSearchInputTarget ? this.searchInputTarget.value.toLowerCase() : ""
    const filteredRows = searchTerm
      ? this.rows.filter(row => row.keyPath.join(" > ").toLowerCase().includes(searchTerm))
      : this.rows

    if (filteredRows.length === 0) {
      this.tableTarget.innerHTML = `
        <tr>
          <td colspan="2" class="py-8 px-4 text-center text-sm text-gray-500">
            ${searchTerm ? "No fields match your search." : "No expected output fields defined."}
          </td>
        </tr>
      `
      return
    }

    this.tableTarget.innerHTML = filteredRows.map(row => this.renderRow(row)).join("")
  }

  renderRow(row) {
    const keyPath = row.keyPath.join(" > ")
    const valuesHtml = row.values.map((value, valueIndex) => this.renderValueInput(row, value, valueIndex)).join("")

    return `
      <tr class="border-b border-gray-200 hover:bg-gray-50" data-row-id="${row.id}">
        <td class="py-1.5 px-2 text-sm text-gray-700 align-top whitespace-nowrap">
          <span class="font-medium">${this.escapeHtml(keyPath)}</span>
        </td>
        <td class="py-1.5 px-2 align-top">
          <div class="flex flex-col gap-1">
            ${valuesHtml}
          </div>
        </td>
      </tr>
    `
  }

  renderValueInput(row, value, valueIndex) {
    const isFirst = valueIndex === 0
    const canRemove = row.values.length > 1

    return `
      <div class="flex items-center gap-1" data-value-index="${valueIndex}">
        ${isFirst ? `
          <select
            class="w-20 px-1 py-1 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            data-action="change->expected-output-editor#matchChanged"
            data-row-id="${row.id}"
          >
            <option value="match" ${row.match === "match" ? "selected" : ""}>match</option>
            <option value="similar" ${row.match === "similar" ? "selected" : ""}>similar</option>
          </select>
        ` : `<div class="w-20"></div>`}
        <input
          type="text"
          value="${this.escapeHtml(value)}"
          class="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          data-action="input->expected-output-editor#valueChanged"
          data-row-id="${row.id}"
          data-value-index="${valueIndex}"
        />
        ${canRemove ? `
          <button
            type="button"
            class="p-1 text-gray-400 hover:text-red-500 focus:outline-none"
            data-action="click->expected-output-editor#removeValue"
            data-row-id="${row.id}"
            data-value-index="${valueIndex}"
            title="Remove value"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        ` : ""}
        ${isFirst ? `
          <button
            type="button"
            class="p-1 text-gray-400 hover:text-blue-500 focus:outline-none"
            data-action="click->expected-output-editor#addValue"
            data-row-id="${row.id}"
            title="Add another value"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
          </button>
        ` : `<div class="w-6"></div>`}
      </div>
    `
  }

  matchChanged(event) {
    const rowId = event.target.dataset.rowId
    const row = this.rows.find(r => r.id === rowId)
    if (row) {
      row.match = event.target.value
      this.updateHiddenInput()
    }
  }

  valueChanged(event) {
    const rowId = event.target.dataset.rowId
    const valueIndex = parseInt(event.target.dataset.valueIndex, 10)
    const row = this.rows.find(r => r.id === rowId)
    if (row) {
      row.values[valueIndex] = event.target.value
      this.updateHiddenInput()
    }
  }

  addValue(event) {
    event.preventDefault()
    const rowId = event.currentTarget.dataset.rowId
    const row = this.rows.find(r => r.id === rowId)
    if (row) {
      row.values.push("")
      this.render()
      this.updateHiddenInput()
      // Focus the new input
      const newInput = this.tableTarget.querySelector(`[data-row-id="${rowId}"][data-value-index="${row.values.length - 1}"]`)
      if (newInput) newInput.focus()
    }
  }

  removeValue(event) {
    event.preventDefault()
    const rowId = event.currentTarget.dataset.rowId
    const valueIndex = parseInt(event.currentTarget.dataset.valueIndex, 10)
    const row = this.rows.find(r => r.id === rowId)
    if (row && row.values.length > 1) {
      row.values.splice(valueIndex, 1)
      this.render()
      this.updateHiddenInput()
    }
  }

  search() {
    this.render()
  }

  // Reconstruct the nested JSON from the flat rows
  reconstructData() {
    const result = {}

    for (const row of this.rows) {
      let current = result
      const pathParts = row.keyPath

      // Navigate/create nested structure
      for (let i = 0; i < pathParts.length - 1; i++) {
        const part = pathParts[i]
        if (!(part in current)) {
          current[part] = {}
        }
        current = current[part]
      }

      // Set the leaf value
      const lastKey = pathParts[pathParts.length - 1]
      const valueContent = row.values.length === 1 ? row.values[0] : row.values
      current[lastKey] = {
        _match: row.match,
        _value: valueContent
      }
    }

    return result
  }

  updateHiddenInput() {
    const data = this.reconstructData()
    this.hiddenInputTarget.value = JSON.stringify(data, null, 2)
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
